import { Loader2, Plus, Sparkles, Trash2 } from "lucide-react";

import { AssistantDisclosureNotice } from "src/components/AssistantChat/AssistantDisclosureNotice";
import { Button } from "src/components/ui/button";
import { ScrollArea } from "src/components/ui/scroll-area";
import { cn } from "src/lib/utils";

export const AssistantSessionList = ({
  sessions,
  selectedThreadId,
  isLoading,
  loadingThreadId,
  activeThreadId,
  archivingThreadIds = new Set(),
  readyThreadIds,
  newChatLoadingId,
  isNewChatDisabled,
  isArchiveDisabled,
  onNewChat,
  onSelectSession,
  onArchiveSession,
  variant = "sidebar",
}) => {
  const isInitialLoading = isLoading && sessions.length === 0;
  const isRefreshing = isLoading && sessions.length > 0;
  const isMobileHistory = variant === "mobile";

  return (
    <div className="flex h-full flex-col">
      <div className={cn(isMobileHistory ? "px-4 pt-4 pb-3" : "p-2")}>
        <Button
          type="button"
          className={cn(
            "w-full cursor-pointer justify-start disabled:cursor-not-allowed",
            isMobileHistory
              ? "h-[52px] gap-3 rounded-xl px-4 text-sm font-semibold"
              : "h-8 gap-2 rounded-md px-3 text-xs",
            !selectedThreadId &&
              (isMobileHistory
                ? "bg-primary/20 ring-primary/20 ring-1"
                : "bg-primary/15")
          )}
          variant="secondary"
          onClick={onNewChat}
          disabled={isNewChatDisabled}
        >
          {loadingThreadId === newChatLoadingId ? (
            <Loader2
              className={cn(
                "animate-spin",
                isMobileHistory ? "size-5" : "size-3.5"
              )}
            />
          ) : (
            <Plus className={cn(isMobileHistory ? "size-5" : "size-3.5")} />
          )}
          {isMobileHistory ? "Start new analysis" : "New chat"}
          {isRefreshing ? (
            <Loader2
              className={cn(
                "text-muted-foreground ml-auto animate-spin",
                isMobileHistory ? "size-4" : "size-3.5"
              )}
            />
          ) : null}
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div
          className={cn(
            isMobileHistory ? "space-y-2 px-4 pb-5" : "space-y-1 p-2"
          )}
        >
          {isInitialLoading ? (
            <div
              className={cn(
                "text-muted-foreground flex items-center gap-2",
                isMobileHistory ? "px-1 py-4 text-sm" : "px-2 py-3 text-xs"
              )}
            >
              <Loader2
                className={cn(
                  "animate-spin",
                  isMobileHistory ? "size-4" : "size-3.5"
                )}
              />
              Loading chats...
            </div>
          ) : null}

          {!isInitialLoading && sessions.length === 0 ? (
            <div
              className={cn(
                "text-muted-foreground",
                isMobileHistory
                  ? "rounded-xl border border-dashed border-border/70 px-4 py-5 text-sm"
                  : "px-2 py-3 text-xs"
              )}
            >
              No saved chats yet. Start with a focused question.
            </div>
          ) : null}

          {isMobileHistory && sessions.length > 0 ? (
            <div className="text-muted-foreground px-1 pt-1 pb-0.5 text-[11px] font-semibold tracking-[0.16em] uppercase">
              Recent analyses
            </div>
          ) : null}

          {sessions.map((session) => {
            const isSessionLoading = loadingThreadId === session.id;
            const isSessionActive = activeThreadId === session.id;
            const isSessionArchiving = archivingThreadIds.has(session.id);
            const isSessionReady =
              readyThreadIds.has(session.id) && selectedThreadId !== session.id;
            const statusIcon = isSessionArchiving ? (
              <Loader2
                className={cn(
                  "text-muted-foreground animate-spin",
                  isMobileHistory ? "size-4" : "size-3.5"
                )}
              />
            ) : isSessionLoading ? (
              <Loader2
                className={cn(
                  "text-muted-foreground animate-spin",
                  isMobileHistory ? "size-4" : "size-3.5"
                )}
              />
            ) : isSessionActive ? (
              <Loader2
                className={cn(
                  "text-primary animate-spin",
                  isMobileHistory ? "size-4" : "size-3.5"
                )}
              />
            ) : isSessionReady ? (
              <Sparkles
                className={cn(
                  "text-primary animate-pulse",
                  isMobileHistory ? "size-4" : "size-3.5"
                )}
              />
            ) : null;

            return (
              <div
                key={session.id}
                className={cn(
                  "group flex items-center gap-0 transition-colors",
                  isMobileHistory
                    ? "min-h-[52px] rounded-xl border border-transparent"
                    : "rounded-md",
                  selectedThreadId === session.id
                    ? isMobileHistory
                      ? "bg-primary/15 text-foreground ring-primary/25 ring-1"
                      : "bg-primary/12 text-foreground"
                    : isSessionReady
                      ? "bg-primary/10 text-foreground ring-primary/25 ring-1"
                      : isMobileHistory
                        ? "bg-card/45 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                )}
              >
                <button
                  type="button"
                  className={cn(
                    "flex min-w-0 flex-1 cursor-pointer items-center text-left",
                    isMobileHistory
                      ? "min-h-[52px] gap-2 px-4 py-3 text-sm font-medium"
                      : "gap-1.5 px-2.5 py-1.5 text-xs"
                  )}
                  onClick={() => onSelectSession(session.id)}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {session.title}
                  </span>
                </button>
                {statusIcon ? (
                  <div
                    className={cn(
                      "flex shrink-0 items-center justify-center",
                      isMobileHistory ? "mr-2 size-10" : "mr-1 size-6"
                    )}
                  >
                    {statusIcon}
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "shrink-0 cursor-pointer transition-opacity disabled:cursor-not-allowed",
                      isMobileHistory
                        ? "mr-2 size-10 opacity-60 hover:opacity-100"
                        : "mr-1 size-6 opacity-0 group-hover:opacity-100"
                    )}
                    onClick={() => onArchiveSession(session.id)}
                    disabled={isArchiveDisabled || isSessionArchiving}
                    aria-label={`Archive ${session.title}`}
                  >
                    <Trash2
                      className={cn(isMobileHistory ? "size-4" : "size-3.5")}
                    />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {!isMobileHistory ? <AssistantDisclosureNotice className="p-3" /> : null}
    </div>
  );
};
